import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { Eye, Package, Search, FileText } from "lucide-react";
import { format } from "date-fns";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import Swal from "sweetalert2";
import { exportConfirmedOrdersToExcel } from "@/features/order/exportExcels";

type Order = {
  id: string;
  order_code: string;
  buyer_name: string;
  buyer_address: string;
  buyer_employee_number: string | null;
  buyer_phone: string;
  quantity: number;
  total_price: number;
  status: string;
  payment_method: string;
  payment_proof_url: string | null;
  created_at: string;
  product_id: string;
  products?: {
    name: string;
    price: number;
  };
};

const fetchOrders = async (): Promise<Order[]> => {
  const { data, error } = await supabase
    .from("orders")
    .select(
      `
      *,
      products (
        name,
        price
      )
    `
    )
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  return data || [];
};

const OrderManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const {
    data: orders = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["orders"],
    queryFn: fetchOrders,
  });

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("orders")
        .update({ status: newStatus })
        .eq("id", orderId);

      if (error) throw error;

      toast({
        title: "Status Updated",
        description: "Order status has been updated successfully",
      });

      refetch();
    } catch (err: any) {
      toast({
        title: "Update Failed",
        description: err.message || "Failed to update order status",
        variant: "destructive",
      });
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.order_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.products?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      {
        variant: "default" | "secondary" | "destructive" | "outline";
        color: string;
      }
    > = {
      pending: { variant: "outline", color: "text-yellow-600" },
      confirmed: { variant: "outline", color: "text-blue-600" },
      shipped: { variant: "secondary", color: "text-purple-600" },
      delivered: { variant: "outline", color: "text-green-600" },
      cancelled: { variant: "outline", color: "text-red-600" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Badge variant={config.variant} className={config.color}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <div className="flex-1">
          <header className="h-12 flex items-center border-b px-4">
            <SidebarTrigger className="mr-2" />
            <h1 className="font-semibold">Order Management</h1>
          </header>

          <main className="flex-1 p-4 md:p-6 space-y-6">
            {/* Header dengan Total */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold tracking-tight md:text-3xl text-foreground">
                  Order Management
                </h1>
                <p className="text-sm text-muted-foreground">
                  Kelola semua pesanan dari pelanggan
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className="px-3 py-1 text-base font-medium whitespace-nowrap"
                >
                  {filteredOrders.length} dari {orders.length} Order
                </Badge>

                <Button
                  variant="secondary"
                  onClick={() => exportConfirmedOrdersToExcel(orders)}
                  className="whitespace-nowrap"
                >
                  Export Confirmed (Excel)
                </Button>
              </div>
            </div>

            {/* Filters */}
            <Card>
              <CardHeader>
                <CardTitle>Filter & Pencarian</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Cari berdasarkan kode order, nama pembeli, atau produk..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-full"
                      />
                    </div>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full lg:w-[200px]">
                      <SelectValue placeholder="Filter berdasarkan status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="shipped">Shipped</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Orders Table - Desktop */}
            <Card className="hidden md:block">
              <CardHeader>
                <CardTitle>Daftar Order ({filteredOrders.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Kode Order</TableHead>
                        <TableHead>Tanggal</TableHead>
                        <TableHead>Pembeli</TableHead>
                        <TableHead>Produk</TableHead>
                        <TableHead>Pembayaran</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Aksi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-sm">
                            {order.order_code}
                          </TableCell>
                          <TableCell>
                            {format(
                              new Date(order.created_at),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {order.buyer_name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {order.buyer_employee_number
                                  ? `NOPEG: ${order.buyer_employee_number}`
                                  : "No NOPEG"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {order.products?.name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Qty: {order.quantity}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">
                                {order.payment_method}
                              </Badge>
                              {order.payment_proof_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    window.open(
                                      order.payment_proof_url!,
                                      "_blank"
                                    )
                                  }
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-bold">
                            Rp{" "}
                            {Number(order.total_price).toLocaleString("id-ID")}
                          </TableCell>
                          <TableCell>{getStatusBadge(order.status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedOrder(order)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {/* <Select
                                value={order.status}
                                onValueChange={(value) => updateOrderStatus(order.id, value)}
                              >
                                <SelectTrigger className="w-[120px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="confirmed">Confirmed</SelectItem>
                                  <SelectItem value="shipped">Shipped</SelectItem>
                                  <SelectItem value="delivered">Delivered</SelectItem>
                                  <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                              </Select> */}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {filteredOrders.length === 0 && (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">
                      Tidak ada order ditemukan
                    </h3>
                    <p className="text-muted-foreground">
                      {orders.length === 0
                        ? "Belum ada order yang masuk."
                        : "Tidak ada order yang sesuai dengan filter saat ini."}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Orders Cards - Mobile */}
            <div className="block md:hidden space-y-4">
              {filteredOrders.map((order) => (
                <Card key={order.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-mono text-sm font-medium">
                          {order.order_code}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(
                            new Date(order.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </div>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>

                    <div className="space-y-2 mb-4">
                      <div>
                        <div className="font-medium">{order.buyer_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {order.buyer_employee_number
                            ? `NOPEG: ${order.buyer_employee_number}`
                            : "No NOPEG"}
                        </div>
                      </div>

                      <div>
                        <div className="font-medium">
                          {order.products?.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Qty: {order.quantity} • {order.payment_method}
                        </div>
                      </div>

                      <div className="font-bold text-lg">
                        Rp {Number(order.total_price).toLocaleString("id-ID")}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        className="flex-1"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Detail
                      </Button>
                      {order.payment_proof_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(order.payment_proof_url!, "_blank")
                          }
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}

              {filteredOrders.length === 0 && (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">
                    Tidak ada order ditemukan
                  </h3>
                  <p className="text-muted-foreground">
                    {orders.length === 0
                      ? "Belum ada order yang masuk."
                      : "Tidak ada order yang sesuai dengan filter saat ini."}
                  </p>
                </div>
              )}
            </div>

            {/* Order Detail Modal */}
            {selectedOrder && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <CardHeader className="relative text-center">
                    <button
                      onClick={() => setSelectedOrder(null)}
                      className="absolute left-0 top-1/2 -translate-y-1/2 text-2xl text-gray-500 hover:text-gray-700 px-4"
                    >
                      &times;
                    </button>
                    <CardTitle className="text-lg font-semibold">
                      Detail Order - {selectedOrder.order_code}
                    </CardTitle>
                  </CardHeader>
                  <hr></hr>
                  <CardContent className="space-y-6 mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-medium mb-2">Informasi Order</h3>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-muted-foreground">
                              Kode Order:
                            </span>{" "}
                            {selectedOrder.order_code}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Tanggal:
                            </span>{" "}
                            {format(
                              new Date(selectedOrder.created_at),
                              "dd/MM/yyyy HH:mm"
                            )}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Status:
                            </span>{" "}
                            {getStatusBadge(selectedOrder.status)}
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium mb-2">Informasi Produk</h3>
                        <div className="space-y-1 text-sm">
                          <div>
                            <span className="text-muted-foreground">
                              Produk:
                            </span>{" "}
                            {selectedOrder.products?.name}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Jumlah:
                            </span>{" "}
                            {selectedOrder.quantity}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Harga Satuan:
                            </span>{" "}
                            Rp{" "}
                            {Number(
                              selectedOrder.products?.price || 0
                            ).toLocaleString("id-ID")}
                          </div>
                          <div>
                            <span className="text-muted-foreground">
                              Total:
                            </span>{" "}
                            <span className="font-bold">
                              Rp{" "}
                              {Number(selectedOrder.total_price).toLocaleString(
                                "id-ID"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2">Informasi Pembeli</h3>
                      <div className="space-y-1 text-sm">
                        <div>
                          <span className="text-muted-foreground">Nama:</span>{" "}
                          {selectedOrder.buyer_name}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Telepon:
                          </span>{" "}
                          {selectedOrder.buyer_phone}
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Nomor Pegawai:
                          </span>{" "}
                          {selectedOrder.buyer_employee_number ||
                            "Tidak tersedia"}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Alamat:</span>{" "}
                          {selectedOrder.buyer_address}
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2">Informasi Pembayaran</h3>
                      <div className="space-y-2">
                        <div className="text-sm">
                          <span className="text-muted-foreground">Metode:</span>
                          <Badge variant="outline" className="ml-2 capitalize">
                            {selectedOrder.payment_method}
                          </Badge>
                        </div>
                        {selectedOrder.payment_proof_url && (
                          <div>
                            <span className="text-sm text-muted-foreground">
                              Bukti Pembayaran:
                            </span>
                            <div className="mt-2">
                              <img
                                src={selectedOrder.payment_proof_url}
                                alt="Bukti pembayaran"
                                className="max-w-full h-auto rounded-lg border"
                                style={{ maxHeight: "300px" }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row pt-4">
                      {/* <Button
                        variant="outline"
                        onClick={() => setSelectedOrder(null)}
                        className="flex-1"
                      >
                        Tutup
                      </Button> */}
                      <Select
                        value={selectedOrder.status}
                        onValueChange={async (value) => {
                          const result = await Swal.fire({
                            title: "Konfirmasi",
                            text: `Apakah Anda yakin ingin mengubah status menjadi "${value}"?`,
                            icon: "question",
                            showCancelButton: true,
                            confirmButtonText: "Ya",
                            cancelButtonText: "Batal",
                          });

                          if (result.isConfirmed) {
                            await updateOrderStatus(selectedOrder.id, value);
                            setSelectedOrder((prev) =>
                              prev ? { ...prev, status: value } : prev
                            );
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default OrderManagement;
